import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../styles/AnalyticsHub.css";

// Optional: future preview hook (safe import if exists)
// import useIntakeQCDashboard from "../../AnalyticsHubHooks/IntakeQCDashboard";

const MODULES = [
  {
    path: "/analytics/grade-out",
    icon: "🌷",
    title: "Daily Grade-out",
    subtitle: "Field harvest · Packhouse grading · Rejections",
    description:
      "Reconcile one field-harvest day across farms, GHSEs, varieties and packhouse lengths, with harvest-type-aware capture dates.",
    color: "#a92f6f",
    lightBg: "#fdf4f9",
    border: "#f0c0d8",
    tag: "RECONCILIATION",
    stats: ["Field Stems", "Graded Stems", "Rejected Stems", "One Day at a Time"],
    badge: "Daily Production",
  },
  {
    path: "/analytics/bunchers",
    icon: "🌾",
    title: "Field / Production",
    subtitle: "Value adder performance · Nets picked · Per bay & greenhouse",
    description:
      "Monitor field harvest data captured by value adders. Leaderboard by nets picked and stems per net. Drill into performance by greenhouse, bay, and variety.",
    color: "#a9ae50",
    lightBg: "#f8faef",
    border: "#d8dca0",
    tag: "FIELD",
    stats: [
      "Value Adder Leaderboard",
      "Per Greenhouse",
      "Per Bay",
      "Per Variety",
      "Field Records",
    ],
    badge: "Value Adders & Nets",
  },

  {
    path: "/analytics/intake",
    icon: "🔬",
    title: "Intake QC Dashboard",
    subtitle: "Intake volumes · QC checks · Quarantine tracking · Pool utilisation",
    description:
      "Full intake and quality control analytics across all farms. Track nets and stems received, inspect approval rates, monitor quarantine queues, and analyse QC check performance in real time.",
    color: "#2e86ab",
    lightBg: "#f0f7fb",
    border: "#aad4e8",
    tag: "INTAKE",
    stats: [
      "Intake Volumes",
      "QC Approval Rate",
      "Quarantine Queue",
      "Pool Utilisation",
      "Split Tracking",
    ],
    badge: "Quality & Intake",
  },

  {
    path: "/analytics/main",
    icon: "📦",
    title: "Packhouse Performance",
    subtitle: "Buncher captures · Worker output · Stems by Length, GHSE & Variety",
    description:
      "Track every bunch captured in the packhouse. Measure worker productivity, stem volumes per length category, greenhouse unit, and variety.",
    color: "#a92f6f",
    lightBg: "#fdf4f9",
    border: "#f0c0d8",
    tag: "PACKHOUSE",
    stats: [
      "Worker Leaderboard",
      "Stems by Length",
      "GHSE Breakdown",
      "Variety Performance",
      "Raw Captures",
    ],
    badge: "Bunchers & Captures",
  },

  {
    path: "/analytics/rejections",
    icon: "⚠️",
    title: "Rejections Analytics",
    subtitle: "Rejection reasons · GHSE · Variety · Historical trends",
    description:
      "Full rejection tracking by reason, greenhouse, variety and farm. Spot patterns, reduce waste, and export reports for quality improvement.",
    color: "#c0392b",
    lightBg: "#fdf4f4",
    border: "#f0c0c0",
    tag: "QC",
    stats: ["By Reason", "By GHSE", "By Variety", "Farm Breakdown", "Trends"],
    badge: "Quality Control",
  },
];

const AnalyticsHub = () => {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(null);

  const today = new Date().toLocaleDateString("en-GB", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="hub-root">
      {/* Background blobs */}
      <div className="hub-blob hub-blob-1" />
      <div className="hub-blob hub-blob-2" />
      <div className="hub-blob hub-blob-3" />

      <div className="hub-content">
        {/* HEADER */}
        <header className="hub-header">
          <div className="hub-header-top">
            <div className="hub-logo-wrap">
              <span className="hub-logo">🌺</span>
            </div>
            <div className="hub-date-chip">{today}</div>
          </div>

          <h1 className="hub-title">Farm Analytics Hub</h1>
          <p className="hub-sub">
            Central intelligence layer for Field, Intake QC, Packhouse and Quality Control
          </p>

          <div className="hub-divider" />
        </header>

        {/* MODULE GRID */}
        <div className="hub-grid">
          {MODULES.map((m, idx) => (
            <button
              key={m.path}
              className={`hub-card ${hovered === idx ? "hub-card-hovered" : ""}`}
              onClick={() => navigate(m.path)}
              onMouseEnter={() => setHovered(idx)}
              onMouseLeave={() => setHovered(null)}
              style={{
                "--card-color": m.color,
                "--card-light-bg": m.lightBg,
                "--card-border": m.border,
                animationDelay: `${idx * 0.08}s`,
              }}
            >
              <div className="hub-card-strip" style={{ background: m.color }} />

              <div className="hub-card-inner">
                {/* TAG */}
                <div className="hub-card-toprow">
                  <span
                    className="hub-card-tag"
                    style={{
                      color: m.color,
                      borderColor: m.border,
                      background: m.lightBg,
                    }}
                  >
                    {m.tag}
                  </span>

                  <span className="hub-card-icon">{m.icon}</span>
                </div>

                {/* TITLE */}
                <h2 className="hub-card-title" style={{ color: m.color }}>
                  {m.title}
                </h2>

                <p className="hub-card-subtitle">{m.subtitle}</p>

                <p className="hub-card-desc">{m.description}</p>

                {/* STATS */}
                <div className="hub-card-tags">
                  {m.stats.map((s) => (
                    <span
                      key={s}
                      className="hub-tag"
                      style={{
                        background: m.lightBg,
                        color: m.color,
                        borderColor: m.border,
                      }}
                    >
                      {s}
                    </span>
                  ))}
                </div>

                {/* FOOTER */}
                <div className="hub-card-footer">
                  <span
                    className="hub-card-badge"
                    style={{ background: m.color }}
                  >
                    {m.badge}
                  </span>

                  <span
                    className="hub-card-arrow"
                    style={{ color: m.color }}
                  >
                    Open Dashboard →
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* LEGEND */}
        <div className="hub-legend">
          <div className="hub-legend-item">
            <span className="hub-legend-dot" style={{ background: "#a9ae50" }} />
            <span><strong>Field</strong> — harvesting performance per value adder</span>
          </div>

          <div className="hub-legend-item">
            <span className="hub-legend-dot" style={{ background: "#2e86ab" }} />
            <span><strong>Intake QC</strong> — intake, QC checks, quarantine + split engine</span>
          </div>

          <div className="hub-legend-item">
            <span className="hub-legend-dot" style={{ background: "#a92f6f" }} />
            <span><strong>Packhouse</strong> — worker captures and production analytics</span>
          </div>

          <div className="hub-legend-item">
            <span className="hub-legend-dot" style={{ background: "#c0392b" }} />
            <span><strong>Rejections</strong> — quality loss analysis & trends</span>
          </div>
        </div>

        {/* FOOTER */}
        <footer className="hub-footer">
          <span>Farm Intelligence System · {new Date().getFullYear()}</span>
        </footer>
      </div>
    </div>
  );
};

export default AnalyticsHub;
